// --------------------------------------------------------------
// Created On: 2026-11-02
// Author: Zachary Thomas
//
// Last Modified: 2026-11-03
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import SystemControlComponentGaugeRadial from "./SystemControlComponentGaugeRadial/SystemControlComponentGaugeRadial";
import SystemControlComponentGaugeSpeedometer from "./SystemControlComponentGaugeSpeedometer/SystemControlComponentGaugeSpeedometer";
import SystemControlComponentGaugeTank from "./SystemControlComponentGaugeTank/SystemControlComponentGaugeTank";
import calculateAttributeHighlightColor from "src/utilities/calculateAttributeHighlightColor";
import formatDateShortLocal from "src/utilities/time/formatDateShortLocal";
import isoUtcIsStale from "src/utilities/time/isoUtcIsStale";
import {
  GAUGE_TYPE_RADIAL,
  GAUGE_TYPE_SPEEDOMETER,
  GAUGE_TYPE_TANK_ROUND,
  GAUGE_TYPE_TANK_SQUARE,
} from "src/constants/systemControl";
import styles from "./SystemControlComponentGauge.module.scss";

// Gauge that displays its value with a graphic that depends on the gauge type, with its timestamp beneath.
export default function SystemControlComponentGauge(props: Props): Component {
  const isStale = props.currentValue === null || isoUtcIsStale(props.currentValue.timestamp);
  const value = props.currentValue?.value ?? null;
  const highlightColor = calculateAttributeHighlightColor(props.highlightingRules, value);

  // Returns how full the gauge is from 0 to 100, based on where the value is between the minimum and maximum.
  function getFillPercent(): number {
    const min = Number(props.min);
    const max = Number(props.max);
    if (value === null || isNaN(Number(value)) || !isFinite(min) || !isFinite(max) || max <= min) {
      return 0;
    } else {
      return Math.min(100, Math.max(0, ((Number(value) - min) / (max - min)) * 100));
    }
  }

  // Returns the class names for the whole gauge.
  function getBodyClassName(): string {
    if (isStale) {
      return `${styles.body} ${styles.stale}`;
    } else {
      return styles.body;
    }
  }

  return (
    <div data-test="system-control-gauge" className={getBodyClassName()}>
      {props.gaugeType === GAUGE_TYPE_RADIAL && (
        <SystemControlComponentGaugeRadial
          value={value}
          unitShortName={props.unitShortName}
          fillPercent={getFillPercent()}
          highlightColor={highlightColor}
          min={props.min}
          max={props.max}
        />
      )}
      {props.gaugeType === GAUGE_TYPE_SPEEDOMETER && (
        <SystemControlComponentGaugeSpeedometer
          value={value}
          unitShortName={props.unitShortName}
          fillPercent={getFillPercent()}
          highlightColor={highlightColor}
          min={props.min}
          max={props.max}
        />
      )}
      {(props.gaugeType === GAUGE_TYPE_TANK_ROUND || props.gaugeType === GAUGE_TYPE_TANK_SQUARE) && (
        <SystemControlComponentGaugeTank
          gaugeType={props.gaugeType}
          value={value}
          unitShortName={props.unitShortName}
          fillPercent={getFillPercent()}
          highlightColor={highlightColor}
          min={props.min}
          max={props.max}
        />
      )}
      <div className={styles.time}>
        {props.currentValue === null || props.currentValue.timestamp === null
          ? "N/A"
          : formatDateShortLocal(props.currentValue.timestamp)}
      </div>
    </div>
  );
}

interface Props {
  currentValue: AttributeValue | null;
  unitShortName: string | null;
  highlightingRules: HighlightingRule[];
  gaugeType: string;
  min: number | string;
  max: number | string;
}

interface AttributeValue {
  value: string | null;
  timestamp: string | null;
}

interface HighlightingRule {
  comparator: string;
  value: string;
  valueType: string;
  colorHexCode: string;
  sortPriority: number;
}
